import React from "react";
import {
  ArrowRight,
  CheckCircle2,
  Target,
  Code2,
  BrainCircuit,
  Rocket,
  BookOpen,
  Globe,
  Zap,
  GraduationCap,
  Briefcase,
  FileText,
  Users,
  Award,
  TrendingUp,
  Sparkles,
  Brain,
  Layers,
  Cloud,
  GitBranch,
  ShieldCheck,
  Box,
  MessageSquare,
  BarChart3,
  Languages,
  Building2,
  ShoppingCart,
  Star
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Footer from "./Footer";
import SaaSHeader from "./SaaSHeader";

const Home = () => {
  const navigate = useNavigate();

  const handleStartLearning = () => {
    navigate("/usertype");
  };

  const FloatingCard = ({ children, delay = 0, className = "" }) => (
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 3 + delay * 0.5, repeat: Infinity, ease: "easeInOut", delay }}
      className={className}
    >
      {children}
    </motion.div>
  );

  return (
    <div className="bg-white text-gray-900 min-h-screen overflow-x-hidden font-sans selection:bg-purple-100 selection:text-purple-900">
      <SaaSHeader />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative w-full flex items-center pt-32 pb-20 px-6 md:px-12 bg-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[60%] h-[80%] bg-gradient-to-bl from-purple-50/60 via-indigo-50/40 to-transparent blur-[100px] rounded-full" />
          <div className="absolute bottom-0 left-0 w-[40%] h-[50%] bg-gradient-to-tr from-blue-50/40 to-transparent blur-[100px] rounded-full" />
          <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: "radial-gradient(#6D4AFF 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        </div>

        <div className="max-w-7xl mx-auto relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

            {/* LEFT */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-purple-50 border border-purple-100 text-purple-700 text-sm font-bold mb-8"
              >
                <Sparkles size={14} className="text-purple-600" />
                AI-Powered Industry Readiness Platform
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="text-4xl md:text-5xl font-black leading-[1.1] tracking-tight mb-5"
              >
                <span className="text-gray-900">Transform Knowledge Into Experience.<br /></span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-500">Become Industry Ready.</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="text-lg text-gray-500 leading-relaxed font-medium mb-4 max-w-xl"
              >
                Whether you're a student in year one, a graduate, or a working professional looking to upskill—<span className="italic font-semibold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600">The Skill Genie is an AI-powered Industry Readiness Platform that bridges the gap from learning to industry readiness through structured learning, coding practice, AI guidance, resume evaluation, and mock interviews.</span>
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="flex flex-wrap gap-3 mb-8"
              >
                {["0–2 Yrs", "3–5 Yrs", "Students", "Professionals"].map((tag, i) => (
                  <span key={i} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-full text-xs font-black text-gray-500 uppercase tracking-wide">
                    {tag}
                  </span>
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.45 }}
                className="flex flex-col sm:flex-row gap-4 mb-8"
              >
                <button
                  onClick={handleStartLearning}
                  className="group flex items-center justify-center gap-2 px-8 py-4 bg-gray-900 text-white rounded-2xl font-bold text-lg hover:bg-black transition-all shadow-xl hover:-translate-y-0.5"
                >
                  Start Learning
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => navigate("/college-intro")}
                  className="flex items-center justify-center px-8 py-4 bg-white text-gray-800 border-2 border-gray-100 rounded-2xl font-bold text-lg hover:border-purple-300 hover:text-purple-700 transition-all"
                >
                  For Institutions
                </button>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-sm text-gray-400 font-medium"
              >
                Industry-grade content · BFSI & E-Commerce domains · Multi-regional language support
              </motion.p>
            </div>

            {/* RIGHT — animated product showcase */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative hidden lg:block"
            >
              <div className="relative h-[560px]">
                <div className="absolute inset-8 bg-gradient-to-br from-purple-100/40 to-indigo-100/30 rounded-[48px] blur-2xl" />

                <FloatingCard delay={0} className="absolute top-0 left-0 w-64">
                  <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                        <Brain size={14} className="text-white" />
                      </div>
                      <div>
                        <div className="text-xs font-black text-gray-900">AI Mentor</div>
                        <div className="text-xs text-green-500 font-bold">● Online</div>
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-3 text-xs text-purple-700 font-medium leading-relaxed">
                      "Your BFSI module is 78% done. Let's tackle transaction processing next."
                    </div>
                  </div>
                </FloatingCard>

                <FloatingCard delay={1} className="absolute top-8 right-0 w-52">
                  <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4">
                    <div className="text-xs font-black text-gray-900 mb-3 flex items-center gap-2">
                      <Target size={12} className="text-indigo-600" /> Career Roadmap
                    </div>
                    {[
                      { label: "Core Java", pct: 100, color: "bg-emerald-500" },
                      { label: "BFSI Domain", pct: 78, color: "bg-indigo-500" },
                      { label: "System Design", pct: 42, color: "bg-purple-500" }
                    ].map((r, i) => (
                      <div key={i} className="mb-2">
                        <div className="flex justify-between text-[10px] text-gray-500 font-bold mb-1">
                          <span>{r.label}</span><span>{r.pct}%</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full">
                          <div className={`h-full ${r.color} rounded-full`} style={{ width: `${r.pct}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </FloatingCard>

                <FloatingCard delay={0.5} className="absolute top-[190px] left-4 w-56">
                  <div className="bg-gray-900 rounded-2xl shadow-xl p-4">
                    <div className="text-xs font-black text-white mb-3 flex items-center gap-2">
                      <GitBranch size={12} className="text-purple-400" /> GitHub Activity
                    </div>
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {Array(28).fill(0).map((_, i) => (
                        <div key={i} className={`w-full aspect-square rounded-sm ${[0,3,5,8,10,13,15,18,20,22,25,27].includes(i) ? "bg-purple-500" : [1,6,11,16,21,26].includes(i) ? "bg-purple-700/50" : "bg-gray-800"}`} />
                      ))}
                    </div>
                    <div className="text-[10px] text-purple-400 font-bold">14 commits this week</div>
                  </div>
                </FloatingCard>

                <FloatingCard delay={1.5} className="absolute top-[150px] right-4 w-60">
                  <div className="bg-gray-950 rounded-2xl shadow-xl border border-gray-800 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                      <div className="w-2 h-2 rounded-full bg-amber-500" />
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-[10px] text-gray-500 ml-1 font-mono">BankService.java</span>
                    </div>
                    <div className="font-mono text-[10px] space-y-1">
                      <div><span className="text-purple-400">public</span> <span className="text-blue-400">Transfer</span><span className="text-gray-400"> process(</span><span className="text-amber-400">Txn</span><span className="text-gray-400"> t) {"{"}</span></div>
                      <div className="pl-4"><span className="text-green-400">validate</span><span className="text-gray-400">(t);</span></div>
                      <div className="pl-4"><span className="text-blue-400">return</span><span className="text-gray-400"> repo.save(t);</span></div>
                      <div><span className="text-gray-400">{"}"}</span></div>
                    </div>
                  </div>
                </FloatingCard>

                <FloatingCard delay={2} className="absolute bottom-20 left-0 w-56">
                  <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4">
                    <div className="text-xs font-black text-gray-900 mb-2 flex items-center gap-2">
                      <MessageSquare size={12} className="text-emerald-600" /> Mock Interview
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="text-3xl font-black text-gray-900">91</div>
                      <div className="text-xs text-emerald-600 font-bold mb-1">/ 100 ↑14</div>
                    </div>
                    <div className="text-[10px] text-gray-400 font-medium">Strong DSA & BFSI domain knowledge</div>
                  </div>
                </FloatingCard>

                <FloatingCard delay={1.2} className="absolute bottom-4 left-1/2 -translate-x-1/2 w-52">
                  <div className="bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl shadow-xl p-4 text-white">
                    <div className="text-xs font-black mb-1 flex items-center gap-2">
                      <Star size={11} /> AI Bootcamp Review
                    </div>
                    <div className="text-[10px] opacity-80 font-medium mt-1">E-Commerce checkout flow — reviewed & approved</div>
                    <div className="mt-2 text-xs font-black text-emerald-300">✓ Production-ready</div>
                  </div>
                </FloatingCard>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── WHAT MAKES TSG DIFFERENT ─────────────────────────────────────── */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 tracking-tight leading-tight mb-4">
              Why The Skill Genie Is<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">Built Differently</span>
            </h2>
            <p className="text-lg text-gray-500 font-medium max-w-2xl">
              Not another video course platform. Every advantage below is live, in use, and exclusive to TSG.
            </p>
          </motion.div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

            {/* Big card — Industry-Grade Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="md:row-span-2 bg-gray-900 rounded-[36px] p-10 flex flex-col justify-between"
            >
              <div>
                <div className="w-12 h-12 bg-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <Star size={22} className="text-white" />
                </div>
                <h3 className="text-2xl font-black text-white mb-4 leading-tight">Industry-Grade Content.<br />Not Hello World.</h3>
                <p className="text-gray-400 font-medium leading-relaxed mb-6">
                  Every concept is taught through real business scenarios, not trivial examples. You write code that solves actual industry problems—the same kind that hiring companies review in technical interviews.
                </p>
                <div className="space-y-3">
                  {[
                    "Production-quality code standards",
                    "Business logic, edge cases & error handling",
                    "Code structured for review & deployment"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm text-gray-300 font-medium">
                      <CheckCircle2 size={15} className="text-purple-400 flex-shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="text-xs font-black text-purple-400 uppercase tracking-widest mb-2">Available Now</div>
                <div className="flex gap-3">
                  <span className="px-4 py-2 bg-white/10 rounded-full text-sm font-black text-white">Java</span>
                  <span className="px-4 py-2 bg-white/10 rounded-full text-sm font-black text-white">Python</span>
                  <span className="px-4 py-2 bg-white/[0.05] border border-white/10 rounded-full text-sm font-bold text-gray-500">+More Soon</span>
                </div>
              </div>
            </motion.div>

            {/* Multi-Regional Language */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm hover:shadow-lg transition-all group"
            >
              <div className="w-11 h-11 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center mb-5 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                <Languages size={22} />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-2">Multi-Regional Language Support</h3>
              <p className="text-gray-500 font-medium text-sm leading-relaxed">
                Learn in your preferred language. Content available across multiple regional languages—making quality tech education accessible without a language barrier.
              </p>
            </motion.div>

            {/* BFSI + E-Commerce */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm hover:shadow-lg transition-all group"
            >
              <div className="flex gap-3 mb-5">
                <div className="w-11 h-11 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-all">
                  <Building2 size={20} />
                </div>
                <div className="w-11 h-11 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all">
                  <ShoppingCart size={20} />
                </div>
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-2">BFSI & E-Commerce Domains</h3>
              <p className="text-gray-500 font-medium text-sm leading-relaxed">
                Domain-specific learning tracks built around Banking, Financial Services & Insurance and E-Commerce—two of India's largest hiring sectors.
              </p>
            </motion.div>

            {/* AI-Reviewed Bootcamp Tasks */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-purple-600 rounded-[32px] p-8 hover:bg-purple-700 transition-all group"
            >
              <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center mb-5">
                <BrainCircuit size={22} className="text-white" />
              </div>
              <h3 className="text-xl font-black text-white mb-2">AI-Reviewed Bootcamp Tasks</h3>
              <p className="text-purple-200 font-medium text-sm leading-relaxed">
                Submit your bootcamp project. Get a detailed AI code review instantly—feedback on logic, structure, edge cases and production readiness. Not just a pass/fail score.
              </p>
            </motion.div>

            {/* AI Mock Interviews */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.25 }}
              className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm hover:shadow-lg transition-all group"
            >
              <div className="w-11 h-11 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center mb-5 group-hover:bg-amber-500 group-hover:text-white transition-all">
                <MessageSquare size={22} />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-2">AI Mock Interviews</h3>
              <p className="text-gray-500 font-medium text-sm leading-relaxed">
                Practice real technical and HR interview scenarios. Get immediate AI feedback on every answer—communication, accuracy and confidence scored together.
              </p>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ── THE GAP ──────────────────────────────────────────────────────── */}
      <section className="py-16 bg-[#0A0C10] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight mb-4 text-white">
              The Gap Between
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400"> Learning and Hiring</span>
            </h2>
            <p className="text-lg text-gray-400 font-medium max-w-2xl mx-auto">
              "Walk me through a project on GitHub. Tell me how you deployed it." Most candidates can't answer this—not because they weren't capable, but because they never had the opportunity. TSG closes that gap before it costs you.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-purple-600/20 rounded-full blur-[50px] hidden lg:block" />

            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-[36px] bg-white/[0.02] border border-white/[0.06]"
            >
              <h3 className="text-xl font-black mb-6 text-gray-500 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gray-800 flex items-center justify-center">
                  <GraduationCap size={18} />
                </div>
                What Most Learning Provides
              </h3>
              <ul className="space-y-4">
                {["Theoretical knowledge", "Concept-based assignments", "Exam-focused evaluation", "Language syntax & programs", "Academic certifications"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-base text-gray-500 font-medium">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-700 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-[36px] bg-gradient-to-br from-purple-900/20 to-indigo-900/20 border border-purple-500/20"
            >
              <h3 className="text-xl font-black mb-6 text-purple-300 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-purple-600 flex items-center justify-center">
                  <Briefcase size={18} className="text-white" />
                </div>
                What Interviewers Actually Ask For
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {["GitHub projects to review", "Deployed applications", "Domain knowledge (BFSI, E-Com)", "Code review experience", "Agile & team workflow", "Problem-solving under pressure", "System design basics", "End-to-end project ownership"].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-base text-white font-medium">
                    <CheckCircle2 size={16} className="text-purple-400 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS — 4 PILLARS ─────────────────────────────────────── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 tracking-tight leading-tight mb-4">
              How TSG Builds<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">Industry Readiness</span>
            </h2>
            <p className="text-lg text-gray-500 font-medium max-w-xl">
              Four pillars. Every learner—0 experience or 5 years—progresses through the same structured path.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                pillar: "Learn",
                headline: "Structured Learning Paths",
                desc: "Roadmaps built around your current knowledge and target role—adapting as you progress. Start from fundamentals or jump to advanced topics based on your experience level.",
                icon: <BookOpen size={26} />,
                bg: "from-purple-50 to-purple-100/40",
                iconBg: "bg-purple-600",
                badge: "Personalized"
              },
              {
                pillar: "Build",
                headline: "Industry-Grade Projects",
                desc: "Build complete applications using real business scenarios—BFSI transactions, e-commerce flows, API development. Every project meets production-quality standards, not hello world.",
                icon: <Code2 size={26} />,
                bg: "from-indigo-50 to-indigo-100/40",
                iconBg: "bg-indigo-600",
                badge: "Production-quality"
              },
              {
                pillar: "Collaborate",
                headline: "Professional Engineering Practices",
                desc: "GitHub workflows, team collaboration, documentation and code reviews—mirror what engineering teams do daily. Build a GitHub history that speaks for you.",
                icon: <GitBranch size={26} />,
                bg: "from-blue-50 to-blue-100/40",
                iconBg: "bg-blue-600",
                badge: "Industry-standard"
              },
              {
                pillar: "Grow",
                headline: "Career Readiness",
                desc: "AI mock interviews, AI-reviewed bootcamp submissions, resume evaluation and skill gap analysis—so you know exactly what you're ready for and what to improve.",
                icon: <TrendingUp size={26} />,
                bg: "from-emerald-50 to-emerald-100/40",
                iconBg: "bg-emerald-600",
                badge: "AI-powered"
              }
            ].map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                whileHover={{ y: -5 }}
                className={`p-9 rounded-[36px] bg-gradient-to-br ${p.bg} border border-gray-100/80 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/5`}
              >
                <div className="flex items-start gap-4 mb-5">
                  <div className={`w-12 h-12 ${p.iconBg} rounded-xl flex items-center justify-center text-white flex-shrink-0 shadow-md`}>
                    {p.icon}
                  </div>
                  <div>
                    <span className="text-xs font-black uppercase tracking-widest text-gray-400 block mb-1">{p.pillar}</span>
                    <h3 className="text-xl font-black text-gray-900 leading-tight">{p.headline}</h3>
                  </div>
                </div>
                <p className="text-gray-600 font-medium leading-relaxed mb-5">{p.desc}</p>
                <span className="inline-flex items-center gap-2 text-xs font-bold text-gray-400 bg-white/60 px-3 py-1.5 rounded-full border border-gray-200">
                  <CheckCircle2 size={12} className="text-emerald-500" />
                  {p.badge}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TECHNOLOGIES ─────────────────────────────────────────────────── */}
      <section className="py-16 bg-gray-900 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight mb-4 text-white">
              Technologies That Shape
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400"> Modern Careers</span>
            </h2>
            <p className="text-lg text-gray-400 font-medium max-w-2xl mx-auto">
              Organized around how the industry actually works—not how textbooks group subjects.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                category: "Artificial Intelligence",
                items: ["Machine Learning", "Generative AI", "Prompt Engineering"],
                why: "AI skills are now expected across every engineering role—not just specialist positions.",
                icon: <Brain size={22} />
              },
              {
                category: "Software Engineering",
                items: ["System Design", "API Development", "Testing & QA"],
                why: "Companies hire for architecture and reliability, not just syntax knowledge.",
                icon: <Code2 size={22} />
              },
              {
                category: "Cloud & DevOps",
                items: ["GitHub", "Docker", "CI/CD & Deployment"],
                why: "Shipping software to production is a fundamental hiring expectation today.",
                icon: <Cloud size={22} />
              },
              {
                category: "Modern Development",
                items: ["Agile Workflows", "Automation", "Team Collaboration"],
                why: "Engineers who work well in teams and improve processes consistently stand out.",
                icon: <Zap size={22} />
              }
            ].map((cat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-7 rounded-[36px] bg-white/[0.03] border border-white/[0.08] flex flex-col h-full hover:bg-white/[0.06] hover:border-white/[0.14] transition-all group"
              >
                <div className="w-11 h-11 bg-purple-600 rounded-xl flex items-center justify-center mb-5 text-white group-hover:scale-110 transition-transform shadow-lg shadow-purple-900/30">
                  {cat.icon}
                </div>
                <h3 className="text-lg font-black mb-3 text-white">{cat.category}</h3>
                <div className="flex flex-wrap gap-2 mb-5">
                  {cat.items.map((item, j) => (
                    <span key={j} className="px-3 py-1 bg-white/[0.06] rounded-full text-xs font-bold text-gray-400">{item}</span>
                  ))}
                </div>
                <div className="mt-auto pt-4 border-t border-white/[0.06]">
                  <p className="text-sm text-gray-400 font-medium leading-relaxed">{cat.why}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AI MENTOR + BOOTCAMP ─────────────────────────────────────────── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">

            {/* Left — AI Mentor */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-purple-50 border border-purple-100 text-purple-700 text-sm font-bold mb-6">
                <BrainCircuit size={14} /> 24/7 Intelligent Support
              </div>
              <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-5 tracking-tight leading-tight">
                Your AI Mentor.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">Always On.</span>
              </h2>
              <p className="text-lg text-gray-500 font-medium leading-relaxed mb-8">
                Not another chatbot. The AI Mentor knows your progress, your domain track, your weak areas and what you're building right now. It provides personalized guidance, instant code feedback, resume review and mock interview coaching—available at any hour.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { title: "Personalized Guidance", icon: <Brain />, desc: "Adapts to your pace and goals" },
                  { title: "Code Feedback", icon: <Code2 />, desc: "Instant review on your submissions" },
                  { title: "Mock Interviews", icon: <MessageSquare />, desc: "AI-scored with detailed feedback" },
                  { title: "Skill Gap Analysis", icon: <BarChart3 />, desc: "Know exactly what to work on next" }
                ].map((card, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.07 }}
                    className="p-5 bg-gray-50 border border-gray-100 rounded-2xl hover:bg-white hover:shadow-lg transition-all group"
                  >
                    <div className="w-9 h-9 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center mb-3 group-hover:bg-purple-600 group-hover:text-white transition-all">
                      {React.cloneElement(card.icon, { size: 18 })}
                    </div>
                    <div className="text-sm font-black text-gray-900 mb-0.5">{card.title}</div>
                    <div className="text-xs text-gray-400 font-medium">{card.desc}</div>
                  </motion.div>
                ))}
              </div>

              <button
                onClick={handleStartLearning}
                className="group flex items-center gap-2 px-7 py-4 bg-gray-900 text-white rounded-2xl font-bold text-base hover:bg-black transition-all shadow-lg"
              >
                Start with AI Mentorship
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>

            {/* Right — Bootcamp */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-sm font-bold mb-6">
                <Rocket size={14} /> Bootcamp Experience
              </div>
              <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-5 tracking-tight leading-tight">
                Build. Get Reviewed.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">Ship It.</span>
              </h2>
              <p className="text-lg text-gray-500 font-medium leading-relaxed mb-8">
                Bootcamps simulate a real engineering team. You scope, build, submit and receive a detailed AI review—then improve and deploy. Every task is a portfolio entry.
              </p>

              <div className="relative p-1 bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 rounded-[40px] shadow-2xl shadow-purple-500/20">
                <div className="bg-white rounded-[36px] p-8">
                  <div className="text-xs font-black text-gray-400 uppercase tracking-widest mb-6">Bootcamp workflow</div>
                  <div className="space-y-5">
                    {[
                      { step: "01", label: "Scope & Plan", desc: "Define the problem and approach" },
                      { step: "02", label: "Build & Code", desc: "Write production-quality software" },
                      { step: "03", label: "AI Review", desc: "Receive detailed code & logic feedback" },
                      { step: "04", label: "Deploy & Portfolio", desc: "Go live, document, present with confidence" }
                    ].map((s, i) => (
                      <div key={i} className="flex items-start gap-4 group">
                        <div className="text-xs font-black text-gray-300 w-6 flex-shrink-0 pt-0.5">{s.step}</div>
                        <div className="w-px self-stretch bg-gray-100 flex-shrink-0" />
                        <div>
                          <div className="font-black text-gray-900 text-sm">{s.label}</div>
                          <div className="text-xs text-gray-400 font-medium">{s.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {["GitHub Integration", "Agile Workflow", "AI Code Review", "Portfolio Ready"].map((tag, i) => (
                      <span key={i} className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-bold">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FOR COLLEGES ─────────────────────────────────────────────────── */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">

            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-sm font-bold mb-6">
                <Award size={14} /> For Institutions
              </div>
              <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-5 tracking-tight leading-tight">
                Help Your Students<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Graduate Hire-Ready</span>
              </h2>
              <p className="text-xl text-gray-600 font-medium leading-relaxed mb-6">
                Your curriculum builds academic strength. The Skill Genie adds the practical layer—projects they've built, tools they've used, interviews they've practised—so placement officers stop hearing "lacks hands-on experience."
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { title: "No curriculum disruption", desc: "TSG works alongside your existing academic program." },
                  { title: "Placement officer dashboard", desc: "Track student progress, project completion and readiness metrics." },
                  { title: "Domain-specific tracks", desc: "BFSI and E-Commerce tracks aligned with your placement sectors." },
                  { title: "Multi-language content", desc: "Regional language support reduces comprehension barriers." }
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="flex gap-4 items-start"
                  >
                    <CheckCircle2 size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-black text-gray-900">{item.title} — </span>
                      <span className="text-gray-500 font-medium">{item.desc}</span>
                    </div>
                  </motion.div>
                ))}
              </div>

              <button
                onClick={() => navigate("/college-intro")}
                className="group flex items-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-2xl font-bold text-lg hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/20 hover:-translate-y-0.5"
              >
                Explore College Program
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-5"
            >
              {[
                { label: "Placement-Ready Graduates", icon: <Award size={22} />, color: "bg-blue-600 text-white", desc: "Students who can demonstrate real projects" },
                { label: "Progress Visibility", icon: <BarChart3 size={22} />, color: "bg-purple-600 text-white", desc: "Cohort-level analytics for faculty" },
                { label: "Domain Alignment", icon: <Building2 size={22} />, color: "bg-indigo-600 text-white", desc: "BFSI & E-Commerce sector tracks" },
                { label: "Industry Standards", icon: <Globe size={22} />, color: "bg-emerald-600 text-white", desc: "Content aligned with hiring expectations" }
              ].map((card, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="p-7 bg-white rounded-[32px] border border-gray-100 shadow-sm hover:shadow-lg transition-all"
                >
                  <div className={`w-12 h-12 ${card.color} rounded-2xl flex items-center justify-center mb-4 shadow-md`}>
                    {card.icon}
                  </div>
                  <h3 className="font-black text-gray-900 mb-1 leading-tight">{card.label}</h3>
                  <p className="text-sm text-gray-400 font-medium">{card.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── OUR PROMISE ──────────────────────────────────────────────────── */}
      <section className="py-24 px-6 bg-white overflow-hidden">
        <div className="max-w-5xl mx-auto">

          {/* Top label */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex justify-center mb-6"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-xs font-bold uppercase tracking-widest">
              <Sparkles size={13} /> Our Promise
            </span>
          </motion.div>

          {/* Main headline */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-center text-3xl md:text-5xl font-black leading-[1.15] tracking-tight mb-6 max-w-3xl mx-auto"
          >
            <span className="text-gray-900">Universities teach Computer Science. </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-500">
              We teach Software Engineering the way the industry practices it.
            </span>
          </motion.h2>

          {/* Divider line */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="w-16 h-1 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full mx-auto mb-16"
          />

          {/* Why TSG — reasons grid */}
          {(() => {
            const reasons = [
              { icon: Target,       text: 'Learn what FAANG and leading product companies expect.' },
              { icon: Code2,        text: 'Solve production-grade engineering tasks, not textbook problems.' },
              { icon: GitBranch,    text: 'Build software using real GitHub workflows — branches, PRs, reviews.' },
              { icon: Brain,        text: 'Receive AI-powered code reviews on every submission.' },
              { icon: ShoppingCart, text: 'Learn through real banking, fintech, and e-commerce scenarios.' },
              { icon: Layers,       text: 'Practice CI/CD, testing, deployment, security, scalability, and system design.' },
              { icon: Award,        text: 'Become interview-ready — not just exam-ready.' },
            ];
            return (
              <div className="mb-14">
                <motion.p
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: 0.15 }}
                  className="text-center text-xs font-bold uppercase tracking-widest text-purple-500 mb-8"
                >
                  Why The Skill Genie?
                </motion.p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {reasons.map(({ icon: Icon, text }, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 18 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: 0.1 + i * 0.07 }}
                      className="flex items-start gap-4 bg-gray-50 hover:bg-purple-50 border border-gray-100 hover:border-purple-200 rounded-2xl px-5 py-4 transition-all duration-200 group"
                    >
                      <div className="w-9 h-9 rounded-xl bg-white group-hover:bg-purple-100 border border-gray-100 group-hover:border-purple-200 flex items-center justify-center flex-shrink-0 shadow-sm transition-all duration-200">
                        <Icon size={17} className="text-purple-600" />
                      </div>
                      <p className="text-sm font-semibold text-gray-700 group-hover:text-gray-900 leading-relaxed transition-colors duration-200 pt-1">
                        {text}
                      </p>
                    </motion.div>
                  ))}

                  {/* Partner CTA card — fills the 8th slot */}
                  <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.1 + 7 * 0.07 }}
                    className="flex items-start gap-4 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl px-5 py-4 shadow-lg shadow-purple-200"
                  >
                    <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <Building2 size={17} className="text-white" />
                    </div>
                    <p className="text-sm font-semibold text-white leading-relaxed pt-1">
                      We're inviting forward-thinking colleges to partner with us and redefine engineering education.
                    </p>
                  </motion.div>
                </div>
              </div>
            );
          })()}

          {/* Bottom promise statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 border border-purple-100 rounded-3xl px-8 py-8 text-center overflow-hidden"
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(139,92,246,0.07)_0%,transparent_70%)] pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 mb-4">
                {[Brain, Code2, Briefcase].map((Icon, i) => (
                  <div key={i} className="w-8 h-8 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Icon size={15} className="text-purple-600" />
                  </div>
                ))}
              </div>
              <p className="text-lg md:text-2xl font-black text-gray-900 leading-snug max-w-2xl mx-auto">
                We don't teach programming languages.{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-500">
                  We build production-ready engineers.
                </span>
              </p>
              <p className="text-gray-500 text-sm mt-3 font-medium max-w-lg mx-auto">
                Every module, every project, every assessment — designed to mirror what top companies expect on day one.
              </p>
            </div>
          </motion.div>

        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gray-900 rounded-[56px] py-16 px-10 md:px-20 text-center relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-purple-600/15 blur-[100px] rounded-full pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[50%] h-[50%] bg-blue-600/15 blur-[100px] rounded-full pointer-events-none" />

            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4 leading-[1.1] tracking-tight">
                Your Degree Opens The Door.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-indigo-400 to-blue-400">
                  Your Skills Shape Your Future.
                </span>
              </h2>
              <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto font-medium leading-relaxed">
                Industry-grade content, real projects, AI mentorship and domain-specific tracks—everything you need to go from where you are to where the industry needs you.
              </p>

              <div className="flex flex-wrap justify-center gap-3 mb-10">
                {["Industry-Grade Content", "BFSI & E-Commerce Domains", "Multi-Regional Language", "AI Mock Interviews", "AI-Reviewed Bootcamp Tasks", "GitHub Portfolio"].map((chip, i) => (
                  <span key={i} className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full text-sm font-bold text-gray-300">
                    <CheckCircle2 size={12} className="text-emerald-400" />
                    {chip}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleStartLearning}
                  className="px-10 py-4 bg-white text-gray-900 rounded-[24px] font-black text-xl hover:scale-105 transition-transform shadow-xl"
                >
                  Start Learning
                </button>
                <button
                  onClick={() => navigate("/college-intro")}
                  className="px-10 py-4 bg-white/10 border border-white/20 text-white rounded-[24px] font-black text-xl hover:bg-white/20 transition-all"
                >
                  For Colleges
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
